import React, { useState } from 'react';
import {
  X,
  Share2,
  Users,
  Shield,
  Check,
  Plus,
  Trash2,
  Lock,
  Mail,
} from 'lucide-react';

export default function DocumentShareModal({
  open,
  document,
  onClose,
  onSaveShares,
}) {
  const [emailInput, setEmailInput] = useState('');
  const [permissionLevel, setPermissionLevel] = useState('view'); // 'view' | 'download' | 'edit' | 'share'
  const [shares, setShares] = useState(document?.shares || []);

  if (!open || !document) return null;

  const handleAddShare = (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!email || !email.includes('@')) return;

    if (shares.some((s) => s.email === email)) {
      setShares(
        shares.map((s) => (s.email === email ? { ...s, permission: permissionLevel } : s))
      );
    } else {
      setShares([
        ...shares,
        {
          id: `share-${Date.now()}`,
          email,
          permission: permissionLevel,
          sharedAt: new Date().toISOString(),
        },
      ]);
    }
    setEmailInput('');
  };

  const handleRemoveShare = (emailToRemove) => {
    setShares(shares.filter((s) => s.email !== emailToRemove));
  };

  const handleSave = () => {
    onSaveShares(document, shares);
    onClose();
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer doc-drawer ios-doc-drawer document-share-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label="Share Secure Document"
      >
        <div className="card-drawer-header modal-header">
          <div className="modal-header-info">
            <div
              className="modal-icon-badge"
              style={{ background: 'rgba(6, 182, 212, 0.15)', color: '#06b6d4' }}
            >
              <Share2 size={18} />
            </div>
            <div className="modal-header-titles">
              <h2 className="modal-title">Share Secure Document</h2>
              <p className="modal-subtitle">
                Grant zero-knowledge encrypted access to "{document.title}".
              </p>
            </div>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="card-drawer-body modal-body document-share-body">
          {/* Add Recipient Form */}
          <form className="share-add-form" onSubmit={handleAddShare}>
            <div className="share-input-row">
              <div className="share-input-wrapper">
                <Mail size={16} className="share-mail-icon" />
                <input
                  type="email"
                  className="form-input share-email-input"
                  placeholder="Enter colleague's email address..."
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                />
              </div>

              <select
                className="form-input share-perm-select"
                value={permissionLevel}
                onChange={(e) => setPermissionLevel(e.target.value)}
              >
                <option value="view">Can View Only</option>
                <option value="download">Can View & Download</option>
                <option value="edit">Can Edit Document</option>
                <option value="share">Full Access & Share</option>
              </select>

              <button
                type="submit"
                className="btn btn-primary btn-sm btn-add-share"
                disabled={!emailInput.trim() || !emailInput.includes('@')}
              >
                <Plus size={15} />
                <span>Grant</span>
              </button>
            </div>
          </form>

          {/* Active Shares List */}
          <div className="active-shares-section">
            <h4 className="active-shares-title">Authorized Collaborators ({shares.length})</h4>

            {shares.length === 0 ? (
              <div className="empty-shares-state">
                <Shield size={30} opacity={0.3} />
                <p>This document is private to your vault. Grant access above to share.</p>
              </div>
            ) : (
              <div className="shares-list">
                {shares.map((sh) => (
                  <div key={sh.email} className="share-row glass-panel">
                    <div className="share-row-left">
                      <div className="share-user-avatar">
                        {sh.email.charAt(0).toUpperCase()}
                      </div>
                      <div className="share-user-info">
                        <span className="share-user-email">{sh.email}</span>
                        <span className="share-user-date">
                          Granted {new Date(sh.sharedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div className="share-row-right">
                      <span className="badge badge-secondary" style={{ textTransform: 'capitalize' }}>
                        {sh.permission}
                      </span>
                      <button
                        type="button"
                        className="btn-icon btn-sm text-danger"
                        onClick={() => handleRemoveShare(sh.email)}
                        title="Revoke access"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="card-drawer-footer modal-footer">
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave}>
            <span>Save Sharing Settings</span>
          </button>
        </div>
      </aside>
    </div>
  );
}
